import {
  useContext, useState, useEffect,
} from 'react';
import { getLength } from 'ol/sphere';
import { DragSourceMonitor } from 'react-dnd';
import { Select, Typography } from 'antd';
import CrossSectionContext from 'contexts/CrossSectionContext';
import DraggableWindow from 'components/DraggableWindow';
import MapViewContext from 'contexts/MapViewContext';
import MainAppContext from 'contexts/MainAppContext';
import ProductsContext from 'contexts/ProductsContext';
import { ColumnWidthOutlined } from '@ant-design/icons';
import { useGetLayerStylesQuery } from 'services/geoserver';
import style from './index.module.scss';
import CrossSectionChart from './components/CrossSectionChart';

const MODAL_HEIGHT = 500;
const MODAL_WIDTH = 700;
const MARGIN = 20;

const {
  header, footer, wrapper, chart,
} = style;

function CrossSectionWindow() {
  const {
    showCrossSectionModal,
    setShowCrossSectionModal,
    segment,
    mousePosition,
    setMousePosition,
    setLastMousePositionUpdateOrigin,
  } = useContext(CrossSectionContext);
  const { mapContainer } = useContext(MapViewContext);
  const { setCrossSectionMode } = useContext(MainAppContext);
  const { activeProducts, getProductById } = useContext(ProductsContext);
  const [activeProduct, setActiveProduct] = useState<number>(activeProducts[0]);
  const activeProductObj = getProductById(activeProduct);
  const { data: allTheStyles } = useGetLayerStylesQuery({ layerName: activeProductObj?.internalName || '' }, { skip: !activeProductObj?.internalName || activeProductObj?.provider === 'dtn' });
  const [activeStyle, setActiveStyle] = useState(getProductById(activeProduct)?.currentStyle);

  const [style, setStyle] = useState<Record<string, number>>({
    top: mapContainer?.current?.clientHeight || 0,
    left: mapContainer?.current?.clientWidth || 0,
  });

  const getSegmentLengthInKilometers = () => {
    let length;
    if (segment) {
      const geometry = segment.getGeometry();
      if (geometry) {
        length = (getLength(geometry) / 1000).toFixed(2);
      }
    }
    return length;
  };

  const handleOnDragEnd = (
    item: {index: string},
    monitor: DragSourceMonitor<{index: string}, unknown>,
  ) => {
    const result : {top: number, left: number} | null = monitor.getDropResult();
    if (result) {
      setStyle((prev) => ({ ...prev, top: prev.top + result.top, left: prev.left + result.left }));
    }
  };

  useEffect(() => {
    setStyle({
      top: (mapContainer?.current?.clientHeight || 0) - MODAL_HEIGHT - MARGIN,
      left: (mapContainer?.current?.clientWidth || 0) - MODAL_WIDTH - MARGIN,
    });
  }, [mapContainer]);

  useEffect(() => {
    if (activeProducts && activeProduct === undefined) {
      setActiveProduct(activeProducts[0]);
      setActiveStyle(getProductById(activeProducts[0])?.currentStyle);
    }
  }, [activeProduct, activeProducts, getProductById]);

  useEffect(() => {
    if (activeProduct !== undefined) {
      setActiveStyle(getProductById(activeProduct)?.currentStyle);
    }
  }, [activeProduct, activeProducts, getProductById]);

  return (
    showCrossSectionModal ? (
      <DraggableWindow
        title="Cross section tool"
        top={style.top as number || 0}
        left={style.left as number || 0}
        width={MODAL_WIDTH}
        onDragEnd={(item, monitor) => handleOnDragEnd(item, monitor)}
        render={() => (
          <div className={wrapper}>
            <div className={header}>
              <div>
                <Typography.Text>Layer:</Typography.Text>
                {' '}
                <Select
                  defaultValue={activeProducts[0]}
                  value={activeProduct}
                  onChange={(value) => setActiveProduct(value)}
                >
                  {activeProducts.map((id) => {
                    const product = getProductById(id);
                    return (
                      <Select.Option
                        key={id}
                        value={product?.index}
                      >
                        {`${product?.workspace} - ${product?.name}`}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
              <div>
                <Typography.Text>Style:</Typography.Text>
                {' '}
                <Select
                  value={activeStyle}
                  onChange={setActiveStyle}
                >
                  {allTheStyles?.map((style) => (
                    <Select.Option
                      key={style.name}
                      value={style.name}
                    >
                      {style.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
            {segment && activeProduct !== undefined && (
              <div className={chart}>
                <CrossSectionChart
                  mousePosition={mousePosition}
                  onMouseOver={(value) => { setMousePosition(value); setLastMousePositionUpdateOrigin('chart'); }}
                />
              </div>
            )}
            <div className={footer} />
          </div>
        )}
        index="0"
        style={{ height: `${MODAL_HEIGHT}px` }}
        closable
        onClose={() => {
          setShowCrossSectionModal(false);
          setCrossSectionMode(false);
        }}
        footer={(
          <>
            <ColumnWidthOutlined />
            {' '}
            {getSegmentLengthInKilometers()}
            {' km'}
          </>
)}
      />
    ) : null
  );
}

export default CrossSectionWindow;
